// The Stores table: DataTable (virtualized, so 10,000 stores keep ~30 rows in the DOM) over the rows the page has
// already filtered, searched and sorted. Sorting is manual: the header reports clicks and the page sorts with the
// column specs (columns.ts), so the same order goes to the CSV export.
//
// Cells only format what the specs and the shared store bits already know; nothing is decoded here (rows carry the
// decoded payload and derived stats). The name cell is a real link (Cmd/middle-click opens a tab) taken out of the
// tab order: the row is the one tab stop and Enter opens it.

import { BadgeCheck, Hand } from 'lucide-react';
import { memo, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import { addressOneLine, recordDisplayName, type RecordPayload } from '@/contracts/models';
import { parseWireDate } from '@/contracts/wireDate';
import { formatShortDate, formatShortDateTime } from '@/domain/format';
import { stageFor } from '@/domain/pipelines';
import { recordSubtitle } from '@/domain/records';
import { lookup } from '@/domain/util';
import { DaysSinceVisit, OwnerAvatar, PriorityChip, RecordTypeBadge, StageChip, storeValueText, visitRecency } from '@/features/common';
import { Badge, Chip, ColorDot, DataTable, cn, type DataTableColumn, type SortingState } from '@/ui';
import {
  customFieldText,
  customValueOf,
  type ColumnSort,
  type StoreColumnContext,
  type StoreColumnSpec,
  type StoreListRow,
} from './columns';
import { nextScheduledText } from './detailModel';
import { NewerAppBadge } from './NewerAppBadge';

export interface StoresTableProps {
  rows: readonly StoreListRow[];
  columns: readonly StoreColumnSpec[];
  ctx: StoreColumnContext;
  columnSort: readonly ColumnSort[];
  onColumnSortChange: (sort: ColumnSort[]) => void;
  /** Where a row leads (the store detail URL). */
  hrefFor: (row: StoreListRow) => string;
  onOpen: (row: StoreListRow, newTab: boolean) => void;
  emptyState: ReactNode;
  /** Router state for the name links (the list query to come back to). */
  linkState?: unknown;
}

const ROW_HEIGHT = 56;

export function StoresTable({ rows, columns, ctx, columnSort, onColumnSortChange, hrefFor, onOpen, emptyState, linkState }: StoresTableProps) {
  const defs = useMemo<DataTableColumn<StoreListRow>[]>(
    () =>
      columns.map((spec) => ({
        id: spec.id,
        header: spec.title,
        // TanStack needs an accessor to offer header sorting; the key is the spec's own (cheap) sort key.
        accessorFn: (row: StoreListRow) => spec.sortKey(row, ctx),
        size: spec.size,
        sortDescFirst: spec.descFirst ?? false,
        meta: { align: spec.align, grow: spec.grow },
        cell: ({ row }: { row: { original: StoreListRow } }) => (
          <Cell spec={spec} row={row.original} ctx={ctx} href={hrefFor(row.original)} linkState={linkState} />
        ),
      })),
    [columns, ctx, hrefFor, linkState],
  );

  const sorting = useMemo<SortingState>(() => columnSort.map((c) => ({ id: c.id, desc: c.desc })), [columnSort]);

  return (
    <DataTable<StoreListRow>
      data={rows}
      columns={defs}
      getRowId={(row) => row.item.id}
      label="Stores"
      sorting={sorting}
      onSortingChange={(next) => onColumnSortChange(next.map((s) => ({ id: s.id, desc: s.desc })))}
      manualSorting
      onRowClick={(row, event) => onOpen(row, event.metaKey || event.ctrlKey)}
      rowHeight={ROW_HEIGHT}
      emptyState={emptyState}
    />
  );
}

// MARK: - Cells

interface CellProps {
  spec: StoreColumnSpec;
  row: StoreListRow;
  ctx: StoreColumnContext;
  href: string;
  linkState: unknown;
}

const Muted = ({ children }: { children?: ReactNode }) => <span className="text-fg-subtle">{children ?? '—'}</span>;

function TextCell({ value, className }: { value: string; className?: string }) {
  if (value === '') return <Muted />;
  return (
    <span className={cn('truncate', className)} title={value}>
      {value}
    </span>
  );
}

function DateCell({ date, now }: { date: Date | undefined; now: Date }) {
  if (!date) return <Muted />;
  return (
    <time dateTime={date.toISOString()} className="truncate tabular-nums" title={formatShortDateTime(date, now)}>
      {formatShortDateTime(date, now)}
    </time>
  );
}

const Cell = memo(function Cell({ spec, row, ctx, href, linkState }: CellProps) {
  const r = row.record;
  const lk = ctx.lookups;
  switch (spec.id) {
    case 'name':
      return <NameCell row={row} href={href} linkState={linkState} />;
    case 'type':
      return <RecordTypeBadge type={r.type} variant="chip" />;
    case 'company':
      return <TextCell value={r.company} />;
    case 'address':
      return <TextCell value={addressOneLine(r.address)} />;
    case 'phone':
      return <TextCell value={r.phone} className="tabular-nums" />;
    case 'email':
      return <TextCell value={r.email} />;
    case 'stage': {
      const stage = stageFor(r, lk.pipelines);
      return stage ? <StageChip stage={stage} /> : <Muted>No stage</Muted>;
    }
    case 'priority':
      return <PriorityChip priority={r.priority} />;
    case 'value': {
      const text = storeValueText(r.value, lk.currencyCode, lk.locale);
      return text ? <span className="font-medium tabular-nums">{text}</span> : <Muted />;
    }
    case 'owner': {
      const owner = lookup(lk.users, r.ownerId);
      return <OwnerAvatar user={owner} presence={owner ? lookup(lk.presence, owner.id) : undefined} showName role="Owner" />;
    }
    case 'territory': {
      const t = lookup(ctx.territories, r.territoryId);
      if (!t) return <Muted />;
      return (
        <span className="inline-flex min-w-0 items-center gap-1.5" title={t.name}>
          <ColorDot color={t.colorHex} size={8} />
          <span className="truncate">{t.name}</span>
        </span>
      );
    }
    case 'visits': {
      const n = row.stats.visitCount;
      const verified = row.stats.verifiedVisitCount;
      return (
        <span
          className={cn('inline-flex items-center gap-1 tabular-nums', n === 0 && 'text-fg-subtle')}
          title={`${n} visit${n === 1 ? '' : 's'}${verified > 0 ? `, ${verified} verified` : ''}`}
        >
          {verified > 0 && <BadgeCheck aria-hidden className="size-3.5 text-success-text" />}
          {n}
        </span>
      );
    }
    case 'lastVisit':
      return <DateCell date={row.stats.lastVisitAt} now={ctx.now} />;
    case 'daysSinceVisit':
      return <DaysSinceVisit format="short" recency={visitRecency(row.stats.lastVisitAt, lk.now)} />;
    case 'lastResult': {
      const result = lookup(lk.visitResults, row.stats.lastVisitResultId);
      return result ? <Chip color={result.colorHex}>{result.name}</Chip> : <Muted />;
    }
    case 'lastActivity':
      return <DateCell date={row.stats.lastActivityAt} now={ctx.now} />;
    case 'nextScheduled': {
      const next = nextScheduledText(row.stats.nextScheduledAt, ctx.now);
      if (!next) return <Muted />;
      return (
        <span className={cn('truncate tabular-nums', next.overdue && 'font-semibold text-danger-text')} title={next.text}>
          {next.overdue ? next.text : next.text.replace(/^Next /, '')}
        </span>
      );
    }
    case 'tags':
      return <TagsCell tags={r.tags} />;
    case 'source':
      return <TextCell value={lookup(ctx.sources, r.sourceId)?.name ?? ''} />;
    case 'created': {
      const d = parseWireDate(r.createdAt);
      return d ? <span className="tabular-nums">{formatShortDate(d)}</span> : <Muted />;
    }
    case 'updated':
      return <DateCell date={parseWireDate(row.item.modifiedAt)} now={ctx.now} />;
    default: {
      const field = spec.field;
      if (!field) return <Muted />;
      const value = field.appliesTo.includes(r.type) ? customValueOf(r, field.id) : undefined;
      return <TextCell value={customFieldText(field, value, lk.currencyCode, lk.locale)} className={spec.align === 'right' ? 'tabular-nums' : undefined} />;
    }
  }
});

function NameCell({ row, href, linkState }: { row: StoreListRow; href: string; linkState: unknown }) {
  const r: RecordPayload = row.record;
  const name = recordDisplayName(r);
  const subtitle = recordSubtitle(r);
  return (
    <Link to={href} state={linkState} tabIndex={-1} className="group flex min-w-0 flex-1 items-center gap-3 outline-none">
      <RecordTypeBadge type={r.type} size={32} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-semibold text-fg group-hover:underline group-hover:underline-offset-2">{name}</span>
          {r.doNotContact && (
            <span role="img" aria-label="Do not contact" title="Do not contact" className="inline-flex shrink-0 text-danger">
              <Hand aria-hidden className="size-3.5" strokeWidth={2.4} />
            </span>
          )}
          {row.item.undecodable !== undefined && <NewerAppBadge />}
          {r.isArchived && (
            <Badge tone="neutral" size="xs">
              Archived
            </Badge>
          )}
        </span>
        {subtitle !== '' && <span className="truncate text-xs text-fg-muted">{subtitle}</span>}
      </span>
    </Link>
  );
}

function TagsCell({ tags }: { tags: readonly string[] }) {
  if (tags.length === 0) return <Muted />;
  const shown = tags.slice(0, 2);
  return (
    <span className="flex min-w-0 items-center gap-1" title={tags.join(', ')}>
      {shown.map((t) => (
        <Chip key={t} color="#009E9E" size="xs">
          {t}
        </Chip>
      ))}
      {tags.length > shown.length && <span className="text-[11px] text-fg-muted">+{tags.length - shown.length}</span>}
    </span>
  );
}
